import { useEffect, useRef } from 'react';

import { AxiosError } from 'axios';
import { useLocation } from 'react-router';

import { PropsWithRequiredChildren } from '@common/types';
import { useAuth } from '@hooks';
import { api } from '@services';

export const AxiosInterceptor = ({ children }: PropsWithRequiredChildren) => {
  const { signOut } = useAuth();
  const { pathname } = useLocation();
  const signOutRef = useRef(signOut);
  const pathnameRef = useRef(pathname);
  const interceptorIdRef = useRef<number | null>(null);

  // Keep refs updated
  useEffect(() => {
    signOutRef.current = signOut;
    pathnameRef.current = pathname;
  }, [signOut, pathname]);

  useEffect(() => {
    // Add response interceptor only once
    const interceptorId = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          (error as AxiosError).response?.status === 401 &&
          pathnameRef.current !== '/'
        ) {
          signOutRef.current({ skipInvalidation: true });
        }

        return Promise.reject(error);
      },
    );

    interceptorIdRef.current = interceptorId;

    return () => {
      // Cleanup interceptor on unmount
      if (interceptorIdRef.current !== null) {
        api.interceptors.response.eject(interceptorIdRef.current);
      }
    };
  }, []);

  return <>{children}</>;
};
